import type { FC } from 'react'
import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useCreate } from '@/api/kubik/credit'
import { FinancingProductError } from '@/components/composed/financing/FinancingProductError'
import { openModal } from '@/components/composed/Modal/actions'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import { FinancingUxFeedbackModal } from '@/components/composed/modals/FinancingUxFeedbackModal/FinancingUxFeedbackModal'
import type { IFinancingUxFeedbackModalProps } from '@/components/composed/modals/FinancingUxFeedbackModal/types'
import { NAV_SUCCESS_NEW_ITEMS } from '@/pages/FinancingAgent/constants'
import { LoanProductOrderMethodCommunication } from '../LoanProductOrderMethodCommunication'
import { LoanProductOrderSuccess } from '../LoanProductOrderSuccess'
import { LoanProductOrderForm } from './components/LoanProductOrderForm'
import { ELoanProductOrderStep } from './constants'
import type { ILoanProductOrderProps } from './types'

addModalToWellKnown({ FinancingUxFeedbackModal })

export const LoanProductOrder: FC<ILoanProductOrderProps> = props => {
  const { product, setIsActiveBlock } = props
  const [stepOrder, setStepOrder] = useState<ELoanProductOrderStep>(ELoanProductOrderStep.FORM)
  const [successFundingRequestId, setSuccessFundingRequestId] = useState<number | undefined>()

  const dispatch = useDispatch()

  const setDefaultStep = useCallback(() => setStepOrder(ELoanProductOrderStep.FORM), [])

  const setSuccessStep = useCallback(() => {
    setStepOrder(ELoanProductOrderStep.SUCCESS)

    dispatch(
      openModal({
        options: { dialogId: 'FinancingUxFeedbackModal', shouldBlockedScroll: false },
        contentOptions: { isHasSkipButton: false } as IFinancingUxFeedbackModalProps['options'],
      }),
    )
  }, [dispatch])

  const setErrorStep = useCallback(() => setStepOrder(ELoanProductOrderStep.ERROR), [])

  const { mutateAsync: createFundingRequest, isLoading } = useCreate({
    mutation: {
      onSuccess: data => {
        setSuccessFundingRequestId(data.data.fundingRequests?.[0]?.id)

        setStepOrder(ELoanProductOrderStep.COMMUNICATION_METHOD)
      },
      onError: () => setStepOrder(ELoanProductOrderStep.ERROR),
    },
  })

  const activeStep = () => {
    switch (stepOrder) {
      case ELoanProductOrderStep.COMMUNICATION_METHOD:
        if (successFundingRequestId) {
          return (
            <LoanProductOrderMethodCommunication
              fundingRequestId={successFundingRequestId}
              onSuccessUpdateFundingRequest={setSuccessStep}
              onErrorUpdateFundingRequest={setErrorStep}
            />
          )
        }

        return null
      case ELoanProductOrderStep.SUCCESS:
        return (
          <LoanProductOrderSuccess
            navItems={NAV_SUCCESS_NEW_ITEMS}
            resetStep={setDefaultStep}
            fundingRequestId={successFundingRequestId}
            withLogo
          />
        )
      case ELoanProductOrderStep.ERROR:
        return <FinancingProductError onResetStep={setDefaultStep} />
      default:
        return (
          <LoanProductOrderForm
            product={product}
            setIsActiveBlock={setIsActiveBlock}
            createFundingRequest={createFundingRequest}
            isLoading={isLoading}
          />
        )
    }
  }

  return <div className="loanProductOrder">{activeStep()}</div>
}
